import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Grid,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { formatDistanceToNow } from "date-fns";
import { EditorState, ContentState } from "draft-js";
import htmlToDraft from "html-to-draftjs";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

// Theme & Global Components
import NavBar from "../../../Components/NavBar";
import { colors } from "../../../../theme";

// Subcomponents
import {
  NewsDetailsHeader,
  NewsDetailsContent,
  NewsDetailsSidebar,
  NewsDetailsEmptyState,
  NewsDetailsSkeleton,
} from "./components";

import "./NewsDetailsStyle.css";

const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const formatTimeAgo = (dateTimeString) => {
  if (!dateTimeString) return "Recently";
  try {
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return "Recently";
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return "Recently";
  }
};

const formatPublishDate = (dateTimeString) => {
  if (!dateTimeString) return "Recently";
  try {
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return "Recently";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "Recently";
  }
};

const calculateReadingTime = (text) => {
  if (!text) return "1 min read";
  const wordsPerMinute = 200;
  const wordCount = text.trim().split(/\s+/).length;
  const minutes = Math.ceil(wordCount / wordsPerMinute);
  return `${Math.max(1, minutes)} min read`;
};

function NewsDetails() {
  const { id } = useParams();
  const [news, setNews] = useState(() => {
    try {
      const savedNews = localStorage.getItem("news");
      if (savedNews) {
        const list = JSON.parse(savedNews);
        return Array.isArray(list)
          ? list.find((item) => String(item.id) === String(id)) || null
          : null;
      }
    } catch {}
    return null;
  });
  const [allNews, setAllNews] = useState(() => {
    try {
      const savedNews = localStorage.getItem("news");
      if (savedNews) {
        const list = JSON.parse(savedNews);
        return Array.isArray(list) ? list : [];
      }
    } catch {}
    return [];
  });
  const [loading, setLoading] = useState(() => {
    try {
      const savedNews = localStorage.getItem("news");
      if (savedNews) {
        const list = JSON.parse(savedNews);
        const found = Array.isArray(list)
          ? list.find((item) => String(item.id) === String(id))
          : null;
        return !found;
      }
    } catch {}
    return true;
  });
  const [editorState, setEditorState] = useState(() => {
    try {
      const savedNews = localStorage.getItem("news");
      if (savedNews) {
        const list = JSON.parse(savedNews);
        const found = Array.isArray(list)
          ? list.find((item) => String(item.id) === String(id))
          : null;
        if (found?.description) {
          const contentBlock = htmlToDraft(found.description);
          if (contentBlock && contentBlock.contentBlocks) {
            const contentState = ContentState.createFromBlockArray(
              contentBlock.contentBlocks
            );
            return EditorState.createWithContent(contentState);
          }
        }
      }
    } catch {}
    return EditorState.createEmpty();
  });
  const [errorMessage, setErrorMessage] = useState("");

  const apiUrl =
    process.env.REACT_APP_API_KEY || "https://testapi.autobetlottery.com/api";

  useEffect(() => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);

    const fetchData = async () => {
      try {
        const response = await fetch(`${apiUrl}/news/${id}`, {
          method: "GET",
          signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (response.ok) {
          const data = await response.json();
          setNews(data);
          const contentBlock = htmlToDraft(data?.description || "");
          if (contentBlock && contentBlock.contentBlocks) {
            const contentState = ContentState.createFromBlockArray(
              contentBlock.contentBlocks
            );
            const editorState = EditorState.createWithContent(contentState);
            setEditorState(editorState);
          }
        } else {
          try {
            const data = await response.json();
            console.error("Error fetching news:", data?.message);
            setErrorMessage(data?.message || "Article not found");
          } catch {
            console.error("Error fetching news:", response.statusText);
          }
        }
      } catch (error) {
        clearTimeout(timeoutId);
        console.error("Error fetching news:", error);
        const savedNews = localStorage.getItem("news");
        if (savedNews) {
          try {
            const list = JSON.parse(savedNews);
            const found = Array.isArray(list)
              ? list.find((item) => String(item.id) === String(id))
              : null;
            if (found) {
              setNews(found);
              const contentBlock = htmlToDraft(found?.description || "");
              if (contentBlock && contentBlock.contentBlocks) {
                const contentState = ContentState.createFromBlockArray(
                  contentBlock.contentBlocks
                );
                const editorState = EditorState.createWithContent(contentState);
                setEditorState(editorState);
              }
            }
          } catch (e) {
            console.error("Cache parsing error:", e);
          }
        }
      } finally {
        setLoading(false);
      }

      // Load related sidebar articles from localStorage if available
      try {
        const savedNews = localStorage.getItem("news");
        if (savedNews) {
          const list = JSON.parse(savedNews);
          if (Array.isArray(list)) {
            setAllNews(list);
          }
        }
      } catch (e) {
        console.warn("Could not load sidebar news from cache:", e);
      }
    };

    fetchData();
    window.scrollTo({ top: 0, behavior: "smooth" });

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [id, apiUrl]);

  // Reading time estimate
  const readingTime = useMemo(() => {
    if (!news || !news.description) return "1 min read";
    const text = stripHtml(news.description);
    return calculateReadingTime(text);
  }, [news]);

  /**
   * Share Handler
   */
  const handleShare = () => {
    const currentUrl = window?.location?.href;
    try {
      navigator.clipboard.writeText(currentUrl);
      toast.success("Article link copied to clipboard!", {
        position: "top-right",
      });
    } catch (err) {
      console.error("Could not copy URL:", err);
    }
  };

  return (
    <Box className="news-details-canvas" sx={{ bgcolor: colors.dashboard.canvas, pb: 8 }}>
      <NavBar />
      <ToastContainer />

      <Container maxWidth="lg" sx={{ pt: { xs: "96px", sm: "108px", md: "116px" }, pb: 6, flexGrow: 1 }}>
        {loading ? (
          <NewsDetailsSkeleton />
        ) : news ? (
          <>
            {/* Header with Breadcrumb, Badges, Title & Meta */}
            <NewsDetailsHeader
              article={news}
              onShare={handleShare}
              readingTime={readingTime}
              formatTimeAgo={formatTimeAgo}
              formatPublishDate={formatPublishDate}
            />

            {/* Content & Sidebar Grid */}
            <Grid container spacing={4}>
              {/* Left Column: Full Body & Featured Image */}
              <Grid item xs={12} lg={8}>
                <NewsDetailsContent
                  article={news}
                  editorState={editorState}
                  onShare={handleShare}
                />
              </Grid>

              {/* Right Column: Latest & Related Stories Sidebar */}
              <Grid item xs={12} lg={4}>
                <NewsDetailsSidebar
                  relatedNews={allNews}
                  currentArticleId={id}
                  formatPublishDate={formatPublishDate}
                />
              </Grid>
            </Grid>
          </>
        ) : (
          <NewsDetailsEmptyState errorMessage={errorMessage} />
        )}
      </Container>
    </Box>
  );
}

export default NewsDetails;
